<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { onClickOutside } from '@vueuse/core'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  options: { type: Array, required: true },
  modelValue: { type: [String, Number], required: true },
  placement: { type: String, default: 'up' },
  visibleItems: { type: Number, default: 10 },
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const containerRef = ref(null)
const listRef = ref(null)
const highlighted = ref(-1)
const searchBuffer = ref('')
let searchTimer = null

const current = computed(() => props.options.find((o) => o.value === props.modelValue) || props.options[0])
const listStyle = computed(() => ({ maxHeight: `${props.visibleItems * 2.25}rem` }))

const select = (value) => {
  emit('update:modelValue', value)
  open.value = false
}

const scrollToItem = (index) => {
  nextTick(() => {
    listRef.value?.children[index]?.scrollIntoView({ block: 'nearest' })
  })
}

const resetSearch = () => {
  searchBuffer.value = ''
  highlighted.value = -1
}

const handleKeydown = (e) => {
  if (!open.value) return
  if (e.key === 'Escape') { open.value = false; return }
  if (e.key === 'Enter' && highlighted.value >= 0) {
    e.preventDefault()
    select(props.options[highlighted.value].value)
    return
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    highlighted.value = Math.min(highlighted.value + 1, props.options.length - 1)
    scrollToItem(highlighted.value)
    return
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault()
    highlighted.value = Math.max(highlighted.value - 1, 0)
    scrollToItem(highlighted.value)
    return
  }
  if (e.key.length === 1) {
    clearTimeout(searchTimer)
    searchBuffer.value += e.key.toLowerCase()
    const match = props.options.findIndex((o) => o.label.toLowerCase().startsWith(searchBuffer.value))
    if (match >= 0) {
      highlighted.value = match
      scrollToItem(match)
    }
    searchTimer = setTimeout(resetSearch, 800)
  }
}

watch(open, (val) => {
  if (val) {
    highlighted.value = props.options.findIndex((o) => o.value === props.modelValue)
    nextTick(() => scrollToItem(highlighted.value))
    window.addEventListener('keydown', handleKeydown)
  } else {
    window.removeEventListener('keydown', handleKeydown)
    resetSearch()
    clearTimeout(searchTimer)
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  clearTimeout(searchTimer)
})

onClickOutside(containerRef, () => { open.value = false })
</script>

<template>
  <div ref="containerRef" class="relative">
    <button
      v-bind="$attrs"
      class="flex items-center gap-2 bg-surface-sunken hover:bg-surface-sunken px-3 py-1.5 rounded-lg text-ink-soft font-medium transition-colors text-xs"
      @click="open = !open"
    >
      <span class="flex-1 text-left truncate">{{ current.label }}</span>
      <Icon
        name="material-symbols:expand-more"
        class="transition-transform shrink-0 text-[13px]"
        :style="{ transform: open ? 'rotate(180deg)' : '' }"
      />
    </button>

    <div
      v-if="open"
      ref="listRef"
      class="absolute right-0 min-w-full bg-surface border border-line rounded-xl shadow-lg overflow-y-auto z-10"
      :class="placement === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'"
      :style="listStyle"
    >
      <button
        v-for="(option, index) in options"
        :key="option.value"
        class="w-full flex items-center text-left px-4 py-2.5 text-xs font-medium text-ink-soft transition-colors"
        :class="{
          'bg-brand-soft text-brand-text font-bold': index === highlighted,
          'bg-surface-alt font-bold text-brand-text': modelValue === option.value && index !== highlighted,
          'hover:bg-surface-alt': index !== highlighted,
        }"
        @click="select(option.value)"
        @mouseenter="highlighted = index"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>
